// Structured rendering of refused actions (409/403/422): policy denials (decision D4),
// BLOCKED-external infrastructure steps, readiness/configuration impediments, version
// conflicts and field errors. The server text is shown as-is; nothing is softened into
// success, and the correlation id is always visible.
import type { ReactNode } from "react";
import { useT } from "../i18n/index.tsx";
import { isApiError, type ApiRequestError } from "../api/client.ts";
import { Banner, localErrorMessage } from "./Feedback.tsx";
import { Button } from "./Button.tsx";
import { readinessCauseText } from "../features/blocks/explain.ts";

export const REAL_PROVIDER_CAUSE_PREFIX = "real provider calls require explicit per-integration approval";

export interface Cause {
  check: string;
  status: string;
  cause: string | null;
}

export interface ParsedError {
  kind: "blocked_external" | "blocked" | "policy_denied" | "impediments" | "version_conflict" | "validation" | "other";
  message: string;
  cause: string | null;
  causes: Cause[];
  impediments: string[];
  steps: { step: string; state: string }[];
  fields: { path: string; message: string }[];
  currentVersion: number | null;
  correlationId: string | null;
  /** Decision reference of a structured BLOCKED refusal (e.g. "D11.1"). */
  decision: string | null;
  /** Server label that qualifies the refusal (e.g. "URL filter, not network isolation"). */
  label: string | null;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

export function parseActionError(error: unknown): ParsedError {
  const base: ParsedError = {
    kind: "other",
    message: error instanceof Error ? error.message : "",
    cause: null,
    causes: [],
    impediments: [],
    steps: [],
    fields: [],
    currentVersion: null,
    correlationId: null,
    decision: null,
    label: null,
  };
  if (!isApiError(error)) return base;
  const e: ApiRequestError = error;
  const d = asRecord(e.details);
  base.correlationId = e.correlationId;
  base.cause = typeof d["cause"] === "string" ? d["cause"] : null;
  if (Array.isArray(d["causes"])) {
    base.causes = d["causes"].map((c) => {
      const o = asRecord(c);
      return { check: String(o["check"] ?? ""), status: String(o["status"] ?? ""), cause: typeof o["cause"] === "string" ? o["cause"] : null };
    });
  }
  if (Array.isArray(d["impediments"])) {
    base.impediments = d["impediments"].map((i) => {
      if (typeof i === "string") return i;
      const o = asRecord(i);
      return [o["path"], o["reason"]].filter((x) => typeof x === "string" && x).join(": ");
    });
  }
  if (Array.isArray(d["pending_gates"])) base.impediments.push(...d["pending_gates"].map(String));
  if (Array.isArray(d["missing"])) base.impediments.push(...d["missing"].map(String));
  if (Array.isArray(d["steps"])) base.steps = d["steps"].map((s) => ({ step: String(asRecord(s)["step"] ?? ""), state: String(asRecord(s)["state"] ?? "") }));
  if (Array.isArray(d["fields"])) base.fields = d["fields"].map((f) => ({ path: String(asRecord(f)["path"] ?? ""), message: String(asRecord(f)["message"] ?? "") }));
  if (typeof d["current_version"] === "number") base.currentVersion = d["current_version"];
  if (typeof d["decision"] === "string") base.decision = d["decision"];
  if (typeof d["egress_control_label"] === "string") base.label = d["egress_control_label"];
  if (d["status"] === "BLOCKED-external" || d["blocked"] === "external") base.kind = "blocked_external";
  else if (d["status"] === "BLOCKED") base.kind = "blocked";
  else if (e.code === "policy_denied") base.kind = "policy_denied";
  else if (e.code === "version_conflict") base.kind = "version_conflict";
  else if (e.code === "validation_failed" || e.code === "bad_request") base.kind = base.fields.length > 0 || e.code === "validation_failed" ? "validation" : "other";
  else if (e.code === "gate_pending") base.kind = "impediments";
  return base;
}

/** Field errors keyed by the first path segment (for inline form errors). */
export function fieldErrors(error: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of parseActionError(error).fields) {
    const key = f.path.split(".")[0] ?? f.path;
    if (key && !out[key]) out[key] = f.message;
  }
  return out;
}

export function ActionError({
  error,
  title,
  onDismiss,
  onReload,
  extra,
  translated,
}: {
  error: unknown;
  title?: ReactNode;
  onDismiss?: () => void;
  onReload?: () => void;
  extra?: ReactNode;
  /** D36.4: a translated message replaces the server's (English) one, which is kept as a technical detail. */
  translated?: { message: string; detail: string | null };
}) {
  const t = useT();
  if (!error) return null;
  const p = parseActionError(error);
  if (isApiError(error) && error.code === "network") p.message = localErrorMessage(error, t);
  const detail = translated && translated.detail && translated.detail !== translated.message ? translated.detail : null;
  if (translated) p.message = translated.message;
  const heading =
    title ??
    (p.kind === "blocked_external"
      ? t("err.blockedExternal")
      : p.kind === "blocked"
        ? t("err.blocked")
      : p.kind === "policy_denied"
        ? t("err.policyDenied")
        : p.kind === "version_conflict"
          ? t("err.versionConflict")
          : p.kind === "impediments"
            ? t("err.impediments")
            : p.kind === "validation"
              ? t("err.validation")
              : t("err.refused"));
  const realProvider = p.kind === "policy_denied" && (p.cause ?? p.message).toLowerCase().includes(REAL_PROVIDER_CAUSE_PREFIX);
  return (
    <Banner
      tone={p.kind === "blocked_external" || p.kind === "blocked" || p.kind === "impediments" || p.kind === "version_conflict" ? "warning" : "error"}
      role="alert"
      title={heading}
      meta={p.correlationId ? t("error.correlation", { id: p.correlationId }) : undefined}
      action={
        <div className="row row--wrap">
          {onReload ? (
            <Button size="sm" onClick={onReload}>
              {t("err.reload")}
            </Button>
          ) : null}
          {onDismiss ? (
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              {t("common.dismiss")}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="stack" style={{ gap: 6 }} data-testid="action-error" data-kind={p.kind}>
        {p.kind === "blocked_external" ? <p>{t("err.blockedExternalBody")}</p> : null}
        {p.kind === "blocked" ? <p>{t("err.blockedBody")}</p> : null}
        {realProvider ? <p>{t("err.realProviderBody")}</p> : null}
        {p.message ? <p className="user-content">{p.message}</p> : null}
        {detail ? <p className="subtle user-content" data-testid="error-detail">{t("apiError.detail", { detail })}</p> : null}
        {p.cause && p.cause !== p.message ? <p className="subtle user-content">{t("err.cause", { cause: p.cause })}</p> : null}
        {p.decision ? <p className="subtle">{t("err.decision", { decision: p.decision })}</p> : null}
        {p.label ? (
          <p>
            <span className="badge badge--warning" data-testid="egress-label">
              {p.label}
            </span>
          </p>
        ) : null}
        {p.kind === "version_conflict" && p.currentVersion !== null ? <p className="subtle">{t("err.currentVersion", { n: p.currentVersion })}</p> : null}
        {p.causes.length > 0 ? (
          <ul className="plain-list" data-testid="error-causes">
            {p.causes.map((c, i) => (
              <li key={`${c.check}-${i}`}>
                <span className="badge mono">{c.check}</span> <span className="subtle">{c.status}</span>
                {c.cause ? <span className="user-content"> — {readinessCauseText(t, c.cause)}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {p.impediments.length > 0 ? (
          <ul className="notes" data-testid="error-impediments">
            {p.impediments.map((i, n) => (
              <li key={n} className="user-content">
                {i}
              </li>
            ))}
          </ul>
        ) : null}
        {p.steps.length > 0 ? (
          <ul className="plain-list" data-testid="error-steps">
            {p.steps.map((s) => (
              <li key={s.step}>
                <span className="badge mono">{s.step}</span> <span className="subtle">{s.state === "not_run" ? t("err.stepNotRun") : s.state}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {p.fields.length > 0 ? (
          <ul className="notes">
            {p.fields.map((f, i) => (
              <li key={i}>
                <span className="mono">{f.path}</span>: {f.message}
              </li>
            ))}
          </ul>
        ) : null}
        {extra}
      </div>
    </Banner>
  );
}
